import React, { useEffect, useState } from 'react';
import { getENachQueue, submitENach } from '../../services/financialservice';
import { getApplicationSanctions } from '../../services/applicationService';
import { Download, ChevronDown, ChevronUp, ExternalLink, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { generateFALPDF } from '../../utils/pdfUtils';

// Component: ENachProcessList
// Lists all applications whose FAL has been accepted and allows finance team
// to initiate eNach by submitting optional link + comments.
// Shows a collapsible panel per application with FAL preview link (PDF generation)
// and discharge settlement summary.

const ENachProcessList = () => {
  const { userData: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // applicationId currently submitting
  const [applications, setApplications] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [formState, setFormState] = useState({}); // { [applicationId]: { link:'', comments:'' } }
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [latestFALUrls, setLatestFALUrls] = useState({}); // Store latest FAL URL for each application

  // Prefer always generating the freshest FAL preview (blob) to avoid stale links
  const openLatestFAL = async (app, fallbackUrl) => {
    try {
      const result = await generateFALPDF(app, { remarks: '', template: 'comprehensive' });
      if (result?.success && result?.blob) {
        const url = URL.createObjectURL(result.blob);
        // Open in new tab; append toolbar params as requested
        const withParams = `${url}#toolbar=0`;
        window.open(withParams, '_blank', 'noopener');
        return;
      }
    } catch (e) {
      // fall through to fallbackUrl
      // console.warn('FAL blob generation failed, falling back to stored URL', e);
    }
    if (fallbackUrl) {
      window.open(`${fallbackUrl}#toolbar=0`, '_blank', 'noopener');
    }
  };

  const load = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await getENachQueue();
      if (res.success) {
        // Sort by submittedAt desc else createdAt
        const items = res.items.sort((a,b) => (b.updatedAt?.seconds||0) - (a.updatedAt?.seconds||0));
        setApplications(items);
        
        // Fetch latest FAL for each application
        const urlMap = {};
        for (const item of items) {
          const appId = item.applicationId || item.applicationID || item.id;
          try {
            const sanctionsRes = await getApplicationSanctions(appId);
            if (sanctionsRes.success && sanctionsRes.sanctions && sanctionsRes.sanctions.length > 0) {
              // Get the latest FAL (approval type) from sanctions, ordered by generatedAt desc
              const latestFAL = sanctionsRes.sanctions.find(s => 
                s.type === 'approval' && s.doctorApproval?.status === 'approved' && s.url
              ) || sanctionsRes.sanctions[0]; // Fallback to first one if no approved FAL found
              
              if (latestFAL?.url) {
                urlMap[appId] = latestFAL.url;
              }
            }
          } catch (e) {
            console.warn(`Failed to fetch latest FAL for ${appId}:`, e);
          }
        }
        setLatestFALUrls(urlMap);
      } else {
        setError(res.error || 'Failed to load');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const updateForm = (appId, field, value) => {
    setFormState(prev => ({
      ...prev,
      [appId]: { ...prev[appId], [field]: value }
    }));
  };

  const handleSubmit = async (applicationId) => {
    const payload = formState[applicationId] || {};
    setSubmitting(applicationId);
    setError(null);
    setSuccessMsg(null);
    try {
      const financeUid = user?.uid || 'finance';
      const res = await submitENach(applicationId, financeUid, payload);
      if (res.success) {
        setSuccessMsg('eNach initiated');
        await load();
      } else {
        setError(res.error || 'Submission failed');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(null);
    }
  };

  const renderDischargeSummary = (app) => {
    const ds = app.workflow?.dischargeSettlement || {};
    if (!ds.finalSettlementAmount && !ds.approvedAmount) return <div className="text-sm text-gray-500">No settlement summary.</div>;
    const billed = ds.totalActualCost || ds.totalActualCost === 0 ? ds.totalActualCost : ds.finalSettlementAmount;
    const final = ds.finalSettlementAmount || ds.approvedAmount;
    const patientPayable = (billed || 0) - (final || 0);
    return (
      <div className="mt-2 text-xs bg-gray-50 rounded p-3 grid grid-cols-2 gap-2">
        <div>
          <div className="text-gray-500">Total Billed</div>
          <div className="font-semibold">₹{billed?.toLocaleString?.() || '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Final Settlement</div>
          <div className="font-semibold text-teal-700">₹{final?.toLocaleString?.() || '—'}</div>
        </div>
        <div className="col-span-2">
          <div className="text-gray-500">Patient Payable</div>
          <div className="font-semibold text-red-600">₹{patientPayable?.toLocaleString?.() || '—'}</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-600">Loading eNach queue...</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">eNach Processing</h1>
          <p className="text-gray-600">Initiate eNach for FAL accepted applications</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => { setRefreshing(true); load(); }} className="btn-outline text-sm">Refresh</button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}
      {successMsg && <div className="p-3 bg-green-50 text-green-700 rounded text-sm">{successMsg}</div>}

      {applications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <p className="text-gray-600">No applications pending eNach initiation.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map(app => {
            const w = app.workflow || {};
            const enach = w.enach || {};
            const appId = app.applicationId || app.applicationID || app.id;
            const isInitiated = enach.status === 'initiated';
            return (
              <div key={app.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => toggle(app.id)}>
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{appId}</div>
                      <div className="text-xs text-gray-500">{app.patient?.name || '—'} • {app.hospitalName || 'Hospital'}</div>
                    </div>
                    {isInitiated && <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">Initiated</span>}
                    {enach.status === 'completed' && <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full border border-green-200">Completed</span>}
                  </div>
                  <button className="text-gray-500 hover:text-gray-700">{expanded[app.id] ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}</button>
                </div>
                {expanded[app.id] && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="grid md:grid-cols-3 gap-4 pt-4">
                      <div className="md:col-span-2 space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-800">Final Approval Letter</h4>
                            {(() => {
                              const falUrl = latestFALUrls[appId] || w.dischargeSettlement?.finalApprovalLetterUrl;
                              const isLatestFAL = !!latestFALUrls[appId];
                              return falUrl || app ? (
                                <div className="flex items-center space-x-2">
                                  {isLatestFAL && (
                                    <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full border border-teal-200 font-medium">
                                      Latest
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openLatestFAL(app, falUrl)}
                                    className="text-xs text-teal-600 hover:underline flex items-center space-x-1"
                                    title={"Open latest FAL document"}
                                  >
                                    <span>Open</span>
                                    <ExternalLink className="h-3 w-3"/>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">No FAL PDF</span>
                              );
                            })()}
                          </div>
                          {renderDischargeSummary(app)}
                        </div>

                        {isInitiated && (
                          <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-3">
                            <div className="font-medium text-indigo-700 mb-1">eNach already initiated</div>
                            {enach.link && <div className="text-indigo-700 break-all">Link: <a className="underline" href={enach.link} target="_blank" rel="noreferrer">{enach.link}</a></div>}
                            {enach.comments && <div className="text-indigo-700 mt-1">Comments: {enach.comments}</div>}
                          </div>
                        )}
                        {enach.status === 'completed' && enach.hospitalProof && (
                          <div className="text-xs bg-green-50 border border-green-200 rounded p-3">
                            <div className="font-medium text-green-700 mb-1">Hospital Uploaded Proof</div>
                            <div className="flex items-center justify-between">
                              <div className="text-green-700 truncate">{enach.hospitalProof.name || 'Proof'}</div>
                              <a href={enach.hospitalProof.url} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline flex items-center space-x-1">
                                <span>Open</span>
                                <ExternalLink className="h-3 w-3"/>
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        {!isInitiated && enach.status !== 'completed' && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Initiate eNach</h4>
                            <div className="space-y-2">
                              <div>
                                <label className="text-xs text-gray-500">eNach Link (optional)</label>
                                <input type="text" value={formState[appId]?.link || ''} onChange={e => updateForm(appId, 'link', e.target.value)} className="input-field text-xs py-1" placeholder="https://..." />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500">Comments (optional)</label>
                                <textarea value={formState[appId]?.comments || ''} onChange={e => updateForm(appId, 'comments', e.target.value)} className="input-field text-xs py-1 h-16" placeholder="Any notes for hospital" />
                              </div>
                              <button disabled={submitting === appId} onClick={() => handleSubmit(appId)} className="w-full bg-teal-600 text-white rounded px-3 py-1.5 text-xs hover:bg-teal-700 flex items-center justify-center space-x-1 disabled:opacity-50">
                                {submitting === appId ? <Loader2 className="h-3 w-3 animate-spin"/> : <Send className="h-3 w-3"/>}
                                <span>{submitting === appId ? 'Submitting...' : 'Initiate eNach'}</span>
                              </button>
                            </div>
                          </div>
                        )}
                        {enach.status === 'completed' && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                            eNach completed by hospital {enach.hospitalCompletedAt?.toDate ? new Date(enach.hospitalCompletedAt.toDate()).toLocaleString() : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ENachProcessList;
